import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Tv2, Globe, Tag } from 'lucide-react';
import { Channel, CHANNEL_TYPE_BADGES } from '../types/channel';
import { apiClient } from '../lib/apiClient';
import SubscribeButton from './SubscribeButton';
import { useChannel } from '../hooks/useChannel';

interface ChannelDirectoryProps {
  myEmail?: string | null;
  /** `slug` is the channel's own tenant. */
  onOpenChannel?: (handle: string, slug: string) => void;
  /** Own channels open in the owner view ("Mi Canal") instead of the visitor view. */
  onOpenMyChannel?: () => void;
}

const isOwnChannel = (channel: Channel, myEmail?: string | null) =>
  !!myEmail && channel.ownerEmail?.toLowerCase() === myEmail.toLowerCase();

const LEVEL_OPTIONS = ['', 'K12', 'HED', 'Postgraduate', 'General'];

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--controls-border, var(--theme-border, #e5e7eb))',
  background: 'var(--controlsBg, var(--card-bg, #fff))',
  color: 'var(--controlsForeground, var(--text-primary, #111))',
  fontSize: 13,
  outline: 'none',
};

// Unique-but-consistent gradient per channel, derived from the handle
const BANNER_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00c6ff 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #667eea 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
  'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
  'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
  'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
];

const getBannerGradient = (handle: string): string => {
  let hash = 0;
  for (let i = 0; i < handle.length; i++) {
    hash = (hash << 5) - hash + handle.charCodeAt(i);
    hash |= 0;
  }
  return BANNER_GRADIENTS[Math.abs(hash) % BANNER_GRADIENTS.length];
};

const AVATAR_SIZE = 48;
const BANNER_HEIGHT = 96;
const AVATAR_OVERLAP = AVATAR_SIZE / 2;

const MAX_VISIBLE_TAGS = 4;

const tagChipStyle: React.CSSProperties = {
  fontSize: 10, padding: '2px 7px', borderRadius: 10,
  background: 'var(--surface-hover, #f3f4f6)',
  color: 'var(--text-secondary, #6b7280)',
  border: '1px solid var(--theme-border, #e5e7eb)',
  whiteSpace: 'nowrap',
};

const ChannelCard: React.FC<{
  channel: Channel;
  myEmail?: string | null;
  onClick: () => void;
}> = ({ channel, myEmail, onClick }) => {
  const { t } = useTranslation();
  // Updated from the Subscribe button so the count changes without reloading the directory.
  const [subscriberCount, setSubscriberCount] = useState(channel.subscriberCount);
  useEffect(() => setSubscriberCount(channel.subscriberCount), [channel.subscriberCount]);
  const initials = channel.displayName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const bannerBg = channel.bannerUrl
    ? `url(${channel.bannerUrl}) center/cover no-repeat`
    : getBannerGradient(channel.handle);

  const visibleTags = channel.tags?.slice(0, MAX_VISIBLE_TAGS) ?? [];
  const hiddenTags  = channel.tags?.slice(MAX_VISIBLE_TAGS) ?? [];

  return (
    <div
      style={{
        background: 'var(--card-bg, var(--theme-card, #fff))',
        border: '1px solid var(--theme-border, #e5e7eb)',
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transition: 'box-shadow 0.18s, transform 0.12s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLDivElement).style.transform = 'none';
      }}
    >
      {/* ── Banner ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        aria-label={t('channel.open_channel_aria', { name: channel.displayName })}
        style={{
          height: BANNER_HEIGHT,
          background: bannerBg,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {CHANNEL_TYPE_BADGES[channel.type] && (
          <span
            aria-hidden
            style={{
              position: 'absolute', top: 8, right: 10,
              fontSize: 16,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
            }}
          >
            {CHANNEL_TYPE_BADGES[channel.type]}
          </span>
        )}

        {/* Avatar — overlaps banner bottom edge */}
        <div style={{
          position: 'absolute',
          bottom: -AVATAR_OVERLAP,
          left: 12,
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: '50%',
          border: '3px solid var(--card-bg, #fff)',
          overflow: 'hidden',
          background: 'var(--theme-primary, #1976d2)',
          flexShrink: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
        }}>
          {channel.avatarUrl ? (
            <img
              src={channel.avatarUrl}
              alt={channel.displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 17, color: '#fff',
            }}>
              {initials}
            </div>
          )}
        </div>
      </div>

      {/* ── Content — flex column, space-between pushes tags to bottom ── */}
      <div style={{
        padding: `${AVATAR_OVERLAP + 8}px 14px 14px`,
        display: 'flex', flexDirection: 'column', flex: 1,
        justifyContent: 'space-between', gap: 6,
      }}>
        {/* Top zone: name + description */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Name row + subscribe */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div
              role="button"
              tabIndex={-1}
              onClick={onClick}
              style={{ flex: 1, minWidth: 0 }}
            >
              <div style={{
                fontWeight: 700, fontSize: 15,
                color: 'var(--card-title-color, var(--text-primary, #111))',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {channel.displayName}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary, #9ca3af)', marginTop: 2 }}>
                @{channel.handle} · {t('channel.video_count', { count: channel.videoCount ?? 0 })} · {subscriberCount} {t('channel.subscribers')}
              </div>
            </div>
            <div style={{ flexShrink: 0, paddingTop: 2 }}>
              {isOwnChannel(channel, myEmail) ? (
                <span style={{ ...tagChipStyle, fontSize: 11, fontWeight: 600 }}>
                  {t('channel.your_channel', 'Your channel')}
                </span>
              ) : (
                <SubscribeButton
                  channel={channel}
                  myEmail={myEmail}
                  onSubscriberCountChange={setSubscriberCount}
                />
              )}
            </div>
          </div>

          {/* Description — 2 lines max, full text on hover */}
          <p
            title={channel.description || undefined}
            style={{
              margin: 0, fontSize: 12,
              color: 'var(--card-text-color, var(--text-secondary, #6b7280))',
              overflow: 'hidden', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              lineHeight: 1.5,
              // Fixed height = 2 lines × 1.5 line-height × 12px font = 36px
              // Ensures cards without description don't collapse
              minHeight: 36,
              cursor: channel.description ? 'help' : 'default',
            }}
          >
            {channel.description ?? ''}
          </p>
        </div>

        {/* Bottom zone: tags — always occupies the same space */}
        <div style={{ height: 22, display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
          {visibleTags.map((tag) => (
            <span key={tag} style={tagChipStyle}>#{tag}</span>
          ))}
          {hiddenTags.length > 0 && (
            <span
              title={hiddenTags.map((t) => `#${t}`).join('  ')}
              style={{
                ...tagChipStyle,
                background: 'color-mix(in srgb, var(--theme-primary, #1976d2) 10%, transparent)',
                color: 'var(--primary-text, #1976d2)',
                borderColor: 'color-mix(in srgb, var(--theme-primary, #1976d2) 25%, transparent)',
                cursor: 'help',
                fontWeight: 600,
              }}
            >
              +{hiddenTags.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ACV-Backend base URL — matches VITE_ACV_BACKEND_API in .env
const ACV_BACKEND_URL = import.meta.env.VITE_ACV_BACKEND_API || 'https://acvnns-api-produc.acv.app';

const ChannelDirectory: React.FC<ChannelDirectoryProps> = ({ myEmail, onOpenChannel, onOpenMyChannel }) => {
  const { t } = useTranslation();
  const { setActiveChannelId } = useChannel();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [subject, setSubject] = useState('');
  const [level, setLevel] = useState('');

  // Phase 13-J: fetch allowed channel handles from ACV-Backend Passport scope endpoint.
  // This is the authoritative source — no dependency on postMessage timing.
  // Empty array = no restriction (user sees all channels).
  const [allowedHandles, setAllowedHandles] = useState<string[]>([]);
  const [scopeReady, setScopeReady] = useState(false);

  useEffect(() => {
    const slug = localStorage.getItem('slug') || apiClient.slug;
    const userEmail = myEmail || localStorage.getItem('userEmail');
    if (!slug || !userEmail) { setScopeReady(true); return; }

    fetch(`${ACV_BACKEND_URL}/api/passport/my-channel-scope?userEmail=${encodeURIComponent(userEmail)}&slug=${encodeURIComponent(slug)}`)
      .then((r) => r.ok ? r.json() : { channels: [] })
      .then((data) => {
        const handles: string[] = (data.channels || [])
          .map((c: { handle?: string }) => (c.handle || '').toLowerCase())
          .filter(Boolean);
        setAllowedHandles(handles);
      })
      .catch(() => { /* non-fatal: fall back to unrestricted */ })
      .finally(() => setScopeReady(true));
  }, [myEmail]);

  const levelLabels: Record<string, string> = {
    '': t('channel.level_all'),
    K12: t('channel.level_k12'),
    HED: t('channel.level_hed'),
    Postgraduate: t('channel.level_postgraduate'),
    General: t('channel.level_general'),
  };

  const search = useCallback(async () => {
    if (!scopeReady) return; // wait for scope resolution before first search
    setLoading(true);
    const result = await apiClient.searchChannels({
      q: q || undefined,
      tags: tagsInput || undefined,
      subject: subject || undefined,
      level: level || undefined,
      allowedHandles: allowedHandles.length > 0 ? allowedHandles : undefined,
    });
    if (result.data) setChannels(result.data);
    setLoading(false);
  }, [q, tagsInput, subject, level, allowedHandles, scopeReady]);

  useEffect(() => {
    const timer = setTimeout(search, 350);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-primary, #f9fafb)' }}>
      {/* Search bar */}
      <div style={{
        padding: '16px 20px',
        background: 'var(--card-bg, var(--theme-card, #fff))',
        borderBottom: '1px solid var(--theme-border, #e5e7eb)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--card-title-color, var(--text-primary, #111))' }}>
          {t('channel.directory_title')}
        </h2>

        {/* Search row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 180, position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary, #9ca3af)', pointerEvents: 'none' }} aria-hidden />
            <input
              style={{ ...inputStyle, width: '100%', paddingLeft: 32, boxSizing: 'border-box' }}
              placeholder={t('channel.search_placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label={t('channel.search_aria')}
            />
          </div>

          <div style={{ flex: 1, minWidth: 130, position: 'relative' }}>
            <Tag size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary, #9ca3af)', pointerEvents: 'none' }} aria-hidden />
            <input
              style={{ ...inputStyle, width: '100%', paddingLeft: 28, boxSizing: 'border-box' }}
              placeholder={t('channel.tags_placeholder')}
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              aria-label={t('channel.tags_aria')}
            />
          </div>

          <div style={{ flex: 1, minWidth: 130, position: 'relative' }}>
            <Globe size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary, #9ca3af)', pointerEvents: 'none' }} aria-hidden />
            <input
              style={{ ...inputStyle, width: '100%', paddingLeft: 28, boxSizing: 'border-box' }}
              placeholder={t('channel.subject_placeholder')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              aria-label={t('channel.subject_aria')}
            />
          </div>

          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            aria-label={t('channel.level_aria')}
            style={{ ...inputStyle, minWidth: 140 }}
          >
            {LEVEL_OPTIONS.map((l) => (
              <option key={l} value={l}>{levelLabels[l]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {loading ? (
          <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>{t('channel.loading_search')}</div>
        ) : channels.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 40 }}>
            <Tv2 size={40} style={{ color: 'var(--text-secondary, #9ca3af)', opacity: 0.4 }} aria-hidden />
            <p style={{ margin: 0, color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>
              {t('channel.no_results')}
            </p>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-secondary, #9ca3af)' }}>
              {channels.length === 1
                ? t('channel.result_count_one')
                : t('channel.result_count_other', { count: channels.length })}
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
              alignItems: 'stretch',
            }}>
              {channels.map((ch) => (
                <ChannelCard
                  key={ch.id}
                  channel={ch}
                  myEmail={myEmail}
                  onClick={() => {
                    if (isOwnChannel(ch, myEmail) && onOpenMyChannel) {
                      setActiveChannelId(ch.id);
                      onOpenMyChannel();
                    } else {
                      onOpenChannel?.(ch.handle, ch.slug);
                    }
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChannelDirectory;
